import api from './api';

/**
 * Inventory operations service for FIFO inventory management
 */

export const inventoryService = {
  /**
   * Add inventory (create new lot)
   */
  addInventory: async (lotData) => {
    const response = await api.post('/inventory/add', lotData);
    return response.data;
  },

  /**
   * Update an existing lot
   */
  updateLot: async (lotId, lotData) => {
    const response = await api.put(`/inventory/lots/${lotId}`, lotData);
    return response.data;
  },

  /**
   * Remove inventory using FIFO method
   */
  removeInventory: async (removalData) => {
    const response = await api.post('/inventory/remove', removalData);
    return response.data;
  },

  /**
   * Get average price for an item
   */
  getAveragePrice: async (itemId) => {
    const response = await api.get(`/inventory/items/${itemId}/average-price`);
    return response.data;
  },

  /**
   * Get all lots for a specific item
   */
  getItemLots: async (itemId, includeEmpty = false) => {
    const response = await api.get(`/inventory/lots/item/${itemId}`, {
      params: { include_empty: includeEmpty }
    });
    return response.data;
  },

  /**
   * Get all lots with optional filters
   */
  getLots: async (params = {}) => {
    const response = await api.get('/inventory/lots', {
      params
    });
    return response.data;
  },

  /**
   * Get transaction ledger with filters
   */
  getTransactions: async (filters = {}) => {
    const response = await api.get('/inventory/transactions', {
      params: filters
    });
    // Return both data and total count from header
    return {
      data: response.data,
      total: parseInt(response.headers['x-total-count'] || '0', 10)
    };
  },

  /**
   * Get credit/debit totals and row count across the entire filtered set of
   * transactions (not limited to the current page), for the ledger footer
   * and CSV export cap check.
   */
  getTransactionSummary: async (filters = {}) => {
    const response = await api.get('/inventory/transactions/summary', {
      params: filters
    });
    return response.data;
  },

  /**
   * Export the entire filtered set of transactions (not just the current
   * page) as a CSV blob, for client-side file download. Accepts the same
   * filters as getTransactions/getTransactionSummary.
   */
  exportTransactions: async (filters = {}) => {
    try {
      const response = await api.get('/inventory/transactions/export', {
        params: filters,
        responseType: 'blob'
      });
      return response.data;
    } catch (err) {
      // With responseType 'blob', axios delivers error bodies (including
      // JSON error payloads like the row-cap message) as a Blob too. Parse
      // it back to JSON here so callers can keep reading
      // err.response.data.detail the same way as every other endpoint.
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          err.response.data = JSON.parse(text);
        } catch {
          // Not parseable JSON; leave the original Blob in place.
        }
      }
      throw err;
    }
  },

  /**
   * Get current inventory summary
   */
  getSummary: async (skip = 0, limit = 50, search = null, category = null) => {
    const params = { skip, limit };
    if (search) params.search = search;
    if (category) params.category = category;

    const response = await api.get('/inventory/summary', { params });
    return {
      data: response.data,
      total: parseInt(response.headers['x-total-count'] || '0', 10)
    };
  },

  /**
   * Get comprehensive value report
   */
  getValueReport: async (search = null, category = null) => {
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    const response = await api.get('/inventory/value-report', { params });
    return response.data;
  },

  /**
   * Bulk reconcile inventory based on physical counts
   */
  bulkReconcile: async (adjustments) => {
    const response = await api.post('/inventory/reconcile', { adjustments });
    return response.data;
  },

  /**
   * Get inventory value as of a specific date or compare between two dates for tax/financial reporting
   */
  getInventoryValueAsOfDate: async (startDate, endDate) => {
    const params = { end_date: endDate };
    if (startDate) {
      params.start_date = startDate;
    }
    const response = await api.get('/inventory/reports/value-as-of-date', { params });
    return response.data;
  }
};
